import { useState } from 'react'
import posthog from 'posthog-js'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { FiCheck, FiCopy } from 'react-icons/fi'
import close_icon from 'assets/images/rectangle/close.svg'
import { describeDay } from 'modules/fund/tabs/transactions/days'
import {
    chargedTotal,
    clockTime,
    IMyTransactionView,
    isCashboxRow,
    jalaliDayKey,
    placeLabel,
} from '../helpers'
import { KindAmount, KindTile, Toman } from '../parts'

interface IDetailsProps {
    item: IDashboardTransactionResult
    view: IMyTransactionView
    me: number | null
    onClose: () => void
}

function Row({ title, value }: { title: string; value: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-4 px-3 py-2.5">
            <dt className="shrink-0 text-[13px] font-normal text-text-secondary">
                {title}
            </dt>
            <dd className="m-0 min-w-0 break-words text-left text-sm font-bold text-black">
                {value}
            </dd>
        </div>
    )
}

export default function TransactionDetails({
    item,
    view,
    me,
    onClose,
}: IDetailsProps) {
    const navigate = useNavigate()
    const [copied, setCopied] = useState(false)

    const day = describeDay(jalaliDayKey(item))
    const time = clockTime(item, true)
    const from = placeLabel(item.source, item, me, 'from')
    const to = placeLabel(item.destination, item, me, 'to')
    const commission = item.total_commission ?? 0
    // The commission is the member's cost only on what they paid; on a loan
    // paid out to them it is the box's.
    const showsCommission = view.kind === 'out' && commission > 0
    const payerName = (item.payer_name ?? '').trim()
    const receiverName = (item.receiver_name ?? '').trim()
    const boxWord = isCashboxRow(item) ? 'صندوق قرعه‌کشی' : 'صندوق'
    const fromBox = item.source === 'hbb' || item.source === 'bba'

    const copyCode = () => {
        // `navigator.clipboard` is undefined outside a secure context and in a
        // few older in-app webviews; calling through it would throw before any
        // `.catch()` could turn that into the toast.
        if (!navigator.clipboard) {
            toast.info('کد را دستی یادداشت کنید')
            return
        }
        navigator.clipboard
            .writeText(item.transaction_code)
            .then(() => {
                setCopied(true)
                posthog.capture('my_transactions_code_copied', {
                    kind: view.kind,
                    ctx_type: item.ctx_type,
                })
                setTimeout(() => setCopied(false), 2000)
            })
            .catch(() => toast.info('کد را دستی یادداشت کنید'))
    }

    const openBox = () => {
        if (!view.boxPath) return
        posthog.capture('my_transactions_fund_opened', {
            kind: view.kind,
            ctx_type: item.ctx_type,
        })
        navigate(view.boxPath)
    }

    return (
        <div className="pb-6 text-right">
            <div className="relative flex flex-col items-center pt-1 text-center">
                <button
                    type="button"
                    aria-label="بستن"
                    className="absolute left-0 top-0 p-1"
                    onClick={onClose}
                >
                    <img className="h-6 w-6" src={close_icon} alt="" />
                </button>
                <KindTile kind={view.kind} large />
                <p className="mb-0 mt-3 text-sm leading-6 text-text-body">
                    {view.subtitle}
                </p>
                <div className="mt-0.5 flex">
                    <KindAmount amount={item.amount} kind={view.kind} large />
                </div>
                <p className="mb-0 mt-1 max-w-full break-words px-8 text-[13px] font-bold leading-6 text-black">
                    {view.title}
                </p>
            </div>

            <dl className="mb-0 mt-4 divide-y divide-divider rounded-card border border-[#E4E7EB]">
                {from && <Row title="از" value={from} />}
                {to && <Row title="به" value={to} />}

                {/* Who else was in it. «از / به» names a bank account's owner
                    already; these are for the payments into a box, where the
                    one who paid and the one it was for can be two people. */}
                {item.payer !== me && payerName && item.source !== 'mba' && (
                    <Row
                        // Out of a box it is the box's money: the manager on
                        // the row sent it, they did not pay it.
                        title={fromBox ? 'ثبت‌کننده' : 'پرداخت‌کننده'}
                        value={payerName}
                    />
                )}
                {item.receiver !== null &&
                    item.receiver !== me &&
                    receiverName &&
                    item.destination !== 'mba' && (
                        <Row title="مربوط به" value={receiverName} />
                    )}

                <Row
                    title="تاریخ"
                    value={day.date ? `${day.title} ${day.date}` : day.title}
                />
                {time && <Row title="ساعت" value={time} />}

                {showsCommission && (
                    <>
                        <Row
                            title="مبلغ"
                            value={<Toman amount={item.amount} />}
                        />
                        <Row
                            title="کارمزد"
                            value={<Toman amount={commission} />}
                        />
                        <Row
                            title="مجموع پرداختی"
                            value={<Toman amount={chargedTotal(item)} />}
                        />
                    </>
                )}

                {item.transaction_code && (
                    <div className="flex items-center justify-between gap-4 px-3 py-2">
                        <dt className="shrink-0 text-[13px] font-normal text-text-secondary">
                            کد پیگیری همیان
                        </dt>
                        <dd className="m-0 flex min-w-0 items-center gap-1.5">
                            <span
                                dir="ltr"
                                className="text-sm font-bold tracking-wider text-black"
                            >
                                {item.transaction_code}
                            </span>
                            <button
                                type="button"
                                onClick={copyCode}
                                aria-label={copied ? 'کپی شد' : 'کپی کد پیگیری'}
                                className="flex h-8 w-8 items-center justify-center rounded-[8px] text-text-secondary active:bg-sunken"
                            >
                                {copied ? (
                                    <FiCheck className="h-4 w-4 text-success-on" />
                                ) : (
                                    <FiCopy className="h-4 w-4" />
                                )}
                            </button>
                        </dd>
                    </div>
                )}
            </dl>

            <div className="mt-5 flex gap-2">
                {view.boxPath ? (
                    <button
                        type="button"
                        onClick={openBox}
                        className="h-12 flex-1 rounded-[10px] bg-green-1 text-sm font-bold text-white"
                    >
                        رفتن به {boxWord}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-12 flex-1 rounded-[10px] bg-green-1 text-sm font-bold text-white"
                    >
                        بازگشت
                    </button>
                )}
            </div>
        </div>
    )
}
